import { inject, Injectable, PLATFORM_ID } from '@angular/core';
import { ActivatedRouteSnapshot, RouterStateSnapshot, UrlTree, Router } from '@angular/router';
import { map, Observable, timer } from 'rxjs';
import { environment } from 'src/environments/environment';
import { AlertService } from './alert.service';
import { AuthService } from './auth.service';
import { isPlatformServer, Location } from '@angular/common';

@Injectable({
  providedIn: 'root'
})
export class AuthGuard {
  private readonly platform = inject(PLATFORM_ID);
  constructor(private auth: AuthService, private router: Router) { }
  canActivate(
    next: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean> | Promise<boolean | UrlTree> | boolean {
    if (isPlatformServer(this.platform)) {
      return false;
    }
    const user = this.auth.getCurrentUser();
    if (user) {
      return true;
    }
    else {
      this.auth.logout();
      return false;
    }
  }
}

@Injectable({
  providedIn: 'root'
})
export class UpgradeExperienceGuard {
  private readonly platform = inject(PLATFORM_ID);
  upgradingExperience: any = environment.upgradingExperience;
  constructor(private auth: AuthService, private router: Router) { }

  canActivate(
    next: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean> | Promise<boolean | UrlTree> | boolean {
    if (isPlatformServer(this.platform)) {
      return false;
    }
    const _data = next.data;
    if (_data.hasOwnProperty("type")) {
      if (this.upgradingExperience[_data.type]) {
        this.router.navigate(["/upgrading"]);
        return false;
      }
    }
    return true;
  }

}

@Injectable({
  providedIn: 'root'
})
export class VerticalsGuard {

  private readonly platform = inject(PLATFORM_ID);
  userVerticals: any = {}

  constructor(
    private alertService: AlertService,
    private router: Router,
    private auth: AuthService
  ) {

  }

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean | UrlTree | Observable<boolean | UrlTree> | Promise<boolean | UrlTree> {
    if (isPlatformServer(this.platform)) {
      return false;
    }
    let _data: any = route.data;
    let _vertical
    this.alertService.getVerticals().subscribe((res) => _vertical = res)
    if (_vertical) {
      if (_vertical[_data.type]) {
        return true;
      } else {
        this.router.navigate(['/products']);
        return false;
      }
    } else {
      return this.auth.getVerticals().pipe(
        map((res: any) => {
          _vertical = res.data.advisor;
          this.alertService.setVerticals(res.data.advisor);
          if (_vertical[_data.type]) {
            return true;
          } else {
            this.router.navigate(['/products']);
            return false;
          }
        }));
    }
  }
}

@Injectable({
  providedIn: 'root'
})
export class AppPageGuard {
  private readonly platform = inject(PLATFORM_ID);
  constructor(
    private router: Router
  ) { }
  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean | UrlTree | Observable<boolean | UrlTree> | Promise<boolean | UrlTree> {
    if (isPlatformServer(this.platform)) {
      return false;
    }
    if (environment.app && (route.routeConfig.path === '/' || route.routeConfig.path === '')) {
      this.router.navigate(['/login'])
      return false
    } else {
      return true
    }
  }
}

@Injectable({
  providedIn: 'root'
})
export class AppVersionGuard {
  private readonly platform = inject(PLATFORM_ID);
  isApp: boolean = environment.app
  constructor(
    private alertService: AlertService,
    private router: Router,
    private location: Location
  ) {

  }
  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean | UrlTree | Observable<boolean | UrlTree> | Promise<boolean | UrlTree> {
    if (isPlatformServer(this.platform)) {
      return false;
    }
    if (this.isApp) {
      let version: any;
      this.alertService.appUpdateAvailable().subscribe((res) => {
        version = res
        timer(500).subscribe(() => {
          if (version) {
            if (state.url === "/app-version") {
              return true
            } else if (!version.forceFlag) {
              if (typeof window !== 'undefined' && !window.sessionStorage.getItem('skipUpdate')) {
                this.router.navigate(['/app-version'])
              }
              return true
            } else {
              this.router.navigate(['/app-version'])
              return false
            }
          } else {
            return true
          }
        })
      })

    } else {
      if (state.url === "/app-version") {
        if (typeof window !== 'undefined') {
          this.location.back()
        }
        return false
      } else {
        return true
      }
    }
  }
}

@Injectable({
  providedIn: 'root'
})
export class UnderInvestpackMaintenance {
  private readonly platform = inject(PLATFORM_ID);
  constructor(
    private router: Router,
    private alertService: AlertService
  ) { }
  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean | UrlTree | Observable<boolean | UrlTree> | Promise<boolean | UrlTree> {
    if (isPlatformServer(this.platform)) {
      return false;
    }
    let vertical
    this.alertService.getVerticals().subscribe((res) => vertical = res)
    if (vertical && vertical.maintenance?.investment?.isMaintenance) {
      if (typeof window !== 'undefined') {
        window.localStorage.setItem('userVertical', 'investment');
      }
      this.router.navigate(['/maintenance'])
      return false
    } else {
      return true
    }
  }
}

@Injectable({
  providedIn: 'root'
})
export class UnderTradingMaintenance {
  private readonly platform = inject(PLATFORM_ID);
  constructor(
    private router: Router,
    private alertService: AlertService
  ) { }
  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean | UrlTree | Observable<boolean | UrlTree> | Promise<boolean | UrlTree> {
    if (isPlatformServer(this.platform)) {
      return false;
    }
    let vertical
    this.alertService.getVerticals().subscribe((res) => vertical = res)
    if (vertical && vertical.maintenance?.trading?.isMaintenance) {
      if (typeof window !== 'undefined') {
        window.localStorage.setItem('userVertical', 'trading');
      }
      this.router.navigate(['/maintenance'])
      return false
    } else {
      return true
    }
  }
}

@Injectable({
  providedIn: 'root'
})
export class UnderContestMaintenance {
  private readonly platform = inject(PLATFORM_ID);
  constructor(
    private router: Router,
    private alertService: AlertService
  ) { }
  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean | UrlTree | Observable<boolean | UrlTree> | Promise<boolean | UrlTree> {
    if (isPlatformServer(this.platform)) {
      return false;
    }
    let vertical
    this.alertService.getVerticals().subscribe((res) => vertical = res)
    if (vertical && vertical.maintenance?.contest?.isMaintenance) {
      if (typeof window !== 'undefined') {
        window.localStorage.setItem('userVertical', '');
      }
      this.router.navigate(['/maintenance'])
      return false
    } else {
      return true
    }
  }
}

@Injectable({
  providedIn: 'root'
})
export class NotForSeprateAdvisorPage {
  private readonly platform = inject(PLATFORM_ID);
  constructor(private router: Router) { }
  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean | UrlTree | Observable<boolean | UrlTree> | Promise<boolean | UrlTree> {
    if (isPlatformServer(this.platform)) {
      return false;
    }
    if (typeof window !== 'undefined') {
      let _advisor = window.sessionStorage.getItem('sai');
      let _return = window.sessionStorage.getItem('saireturnUrl');
      let _category = window.sessionStorage.getItem('saicategory');
      if (_advisor) {
        let _extra = {};
        if (_category) {
          _extra = { queryParams: { category: _category } }
        }
        if (_return.indexOf('trading') > -1)
          _return = '/trading/packs';
        this.router.navigate([_return], _extra);
        return false;
      }
      return true;
    }
  }
}

@Injectable({
  providedIn: 'root'
})
export class NotForRMFlow {
  private readonly platform = inject(PLATFORM_ID);
  constructor(private router: Router) { }
  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean | UrlTree | Observable<boolean | UrlTree> | Promise<boolean | UrlTree> {
    if (isPlatformServer(this.platform)) {
      return false;
    }
    if (typeof window !== 'undefined') {
      let _rmInfo = window.sessionStorage.getItem('rmf') ? JSON.parse(window.sessionStorage.getItem('rmf')) : null;
      if (_rmInfo) {
        if (_rmInfo.journeyType == 'complete_profile') {
          this.router.navigate(['/profile-completion']);
        }
        else if (_rmInfo.journeyType == 'investpack_subscription') {
          this.router.navigate(['/investment/investpack', _rmInfo.investpackId]);
        }
        else if (_rmInfo.journeyType == 'investpack_investment') {
          this.router.navigate(['/investment/investpack', _rmInfo.investpackId]);
        }
        else if (_rmInfo.journeyType == 'investpack_exploration') {
          this.router.navigate(['/investment/investpack']);
        }
        else if (_rmInfo.journeyType == 'investpack_rebalancing') {
          this.router.navigate(['/investment/investpack', _rmInfo.investpackId]);
        }
        else if (_rmInfo.journeyType == 'investpack_exit') {
          this.router.navigate(['/investment/investpack', _rmInfo.investpackId]);
        }
        return false;
      }
      return true;
    }
  }
}